import { type ReactElement, useState } from "react"

import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { CircularProgress } from "@mui/material"
import { NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { HistoryDialogHeader, VersioningDialog } from "../../../components/dialogs/VersioningDialog"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { VersionChangeRequest } from "../../../types/custom/rules"
import type { CalculatorVersionRetrieve } from "../../../types/generated/api/CalculatorVersionRetrieve"
import type { PaginatedCalculatorVersionRetrieveList } from "../../../types/generated/api/PaginatedCalculatorVersionRetrieveList"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"
import type { HistoryDialogProps, VersionProps } from "../interfaces"
import { ConditionCard } from "./ConditionCard"

import styles from "./HistoryDialog.module.scss"

function Version(props: Readonly<VersionProps>): ReactElement {
  const { version, calculatorId } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { isLoading: isVersionLoading, data: versionData } = useQuery<
    AxiosResponse<CalculatorVersionRetrieve>,
    AxiosError
  >(
    ["calculatorVersion", calculatorId, version],
    () =>
      KonanAPI.retrieveCalculatorVersion({
        projectId: projectId as string,
        calculatorId: calculatorId as string,
        version: version as string,
      }),
    {
      enabled: !!projectId && !!calculatorId && !!version,
    },
  )

  if (isVersionLoading) {
    return (
      <div className={styles.loadingStateLayout}>
        <CircularProgress size={32} color="primary" />
        <Typography variant={"h3-regular"}>Fetching your data...</Typography>
      </div>
    )
  }

  if (versionData?.data.equations?.length === 0) {
    return (
      <div className={styles.loadingStateLayout}>
        <KonanEmptyState title={"Version has no data"} />
      </div>
    )
  }

  return (
    <div className={styles.versionHistoryLayout}>
      {versionData?.data.equations?.map((equation, index) => (
        <ConditionCard
          key={`${versionData?.data.version}-${index}`}
          name={equation.computed_feature_name}
          equation={equation.equation}
          order={index + 1}
          isLastCard={index === (versionData?.data.equations?.length ? versionData?.data.equations?.length : 0) - 1}
        />
      ))}
    </div>
  )
}

export function HistoryDialog(props: Readonly<HistoryDialogProps>): ReactElement {
  const { name, uuid, activeVersion, onClose } = props
  const { id: projectId } = useParams<{ id: string }>()

  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(activeVersion.version)

  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isCalculatorVersionsLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  const {
    isLoading: isCalculatorVersionsLoading,
    isFetchingNextPage,
    data: calculatorVersionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedCalculatorVersionRetrieveList>, AxiosError, CalculatorVersionRetrieve>(
    ["calculatorVersionsList", projectId, uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.listCalculatorVersions({
        projectId: projectId as string,
        calculatorId: uuid as string,
        page_size: 20,
        page: pageParam,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<CalculatorVersionRetrieve[]>(
          (accumulator, page) => accumulator.concat(page.data.results as CalculatorVersionRetrieve[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!uuid,
      refetchOnMount: true,
    },
  )

  // change ruleset version
  const versionChangeMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
    {
      onSuccess: async () => {
        NotificationUtils.toast(`Version (${selectedVersion}) restored.`, {
          severity: "success",
        })

        queryClient.invalidateQueries(["calculatorVersionsList", projectId, uuid])
        await queryClient.invalidateQueries(["calculator", uuid])
      },
      onError: () => {
        NotificationUtils.toast(`Couldn't restore version (${selectedVersion}). Please try again later`, {
          severity: "error",
        })
      },
    },
  )

  return (
    <VersioningDialog
      activeVersion={activeVersion}
      setSelectedVersion={setSelectedVersion}
      selectedVersion={selectedVersion}
      isOpen={true}
      versions={calculatorVersionsList?.pages ?? []}
      onClose={() => onClose()}
      infiniteScrollProps={{
        hasNextPage,
        isFetchingNextPage,
        lastVersionRef,
      }}
    >
      <HistoryDialogHeader
        name={name}
        isActiveVersion={selectedVersion === activeVersion.version}
        version={selectedVersion as string}
        onRestore={() =>
          versionChangeMutation.mutateAsync({
            projectId: projectId as string,
            resourceId: uuid as string,
            resource: "calculators",
            version: selectedVersion as string,
          })
        }
        isRestoring={versionChangeMutation.isLoading}
      />
      <Version version={selectedVersion} calculatorId={uuid} />
    </VersioningDialog>
  )
}
